import { View, StyleSheet, Text } from 'react-native';
import { Scorecard, ScorecardRow, theme } from '@socrait/react';

import { ScorecardType, StudentWithCount, StudentWithAttendance, AttendanceData } from '~/services/types';
import { ReviewScorecardsResponse, ReviewScorecardsMeeting } from '~/services/review/useReviewScorecardsQuery';
import { getMeetingContentState, MeetingContentState } from '~/pages/review/utils/meetingContentState';

import { AttendanceScorecard } from '~/pages/review/ui/AttendanceScorecard';

const sortStudentData = <T extends StudentWithCount | StudentWithAttendance>(data: T[]) => {
  return data.slice().sort((a, b) => {
    if (!!(a as StudentWithCount).count && !!(b as StudentWithCount).count) {
      // Primary sort: by count (descending)
      const countDiff = (b as StudentWithCount).count - (a as StudentWithCount).count;
      if (countDiff !== 0) return countDiff;
    }

    // Secondary sort: alphabetically by name (ascending)
    return a.student.voiceName.localeCompare(b.student.voiceName);
  });
};

const getAttendanceData = (data: StudentWithAttendance[]): AttendanceData[] => {
  const present = sortStudentData(data.filter((item) => item.status === 'present'));
  const absent = sortStudentData(data.filter((item) => item.status === 'absent'));
  // TODO: uncomment for phase 3
  // const unsure = sortStudentData(data.filter((item) => item.status === 'unsure'));

  return [
    { attendanceType: 'present', title: 'Present', data: present },
    { attendanceType: 'absent', title: 'Absent', data: absent },
    // TODO: uncomment for phase 3
    // { attendanceType: 'unsure', title: 'Unsure', data: unsure },
  ];
};

export const getDisplayText = (type: ScorecardType, attendanceData?: AttendanceData[], total?: number) => {
  if (type === 'attendance') {
    const presentData = attendanceData?.find((datum) => datum.attendanceType === 'present')?.data;

    if (presentData?.length && presentData?.length === total) {
      return 'All students are present';
    }

    return 'No attendance data collected';
  }
  return 'None detected';
};

/** Text shown in place of scorecard rows when the selected meeting has no content yet
 *  (or the day has no meetings at all). Never treats a `processing` meeting as an error. */
export const getScorecardText = (
  type: ScorecardType,
  state: MeetingContentState<ReviewScorecardsMeeting>,
  attendanceData?: AttendanceData[],
  total?: number,
): string => {
  switch (state.kind) {
    case 'no-meetings':
      return state.dayStatus === 'processed' ? 'No class content found for this day.' : 'Waiting for data';
    case 'processing':
      return 'Processing…';
    case 'failed':
      return 'Data unavailable';
    case 'content':
      return getDisplayText(type, attendanceData, total);
  }
};

const getScorecardVariant = (type: ScorecardType) => {
  switch (type) {
    case 'attendance':
      return 'navy';
    case 'warnings':
      return 'yellow';
    case 'praises':
      return 'purple';
    default:
      return 'navy';
  }
};

const getBehaviorTotal = (data?: StudentWithCount[]) => {
  if (!data) return undefined;

  return data.reduce((acc, curr) => {
    const { count } = curr as StudentWithCount;
    return acc + (count || 0);
  }, 0);
};

const renderScorecard = (
  title: string,
  type: ScorecardType,
  contentState: MeetingContentState<ReviewScorecardsMeeting>,
  data?: StudentWithCount[],
) => {
  const scorecardVariant = getScorecardVariant(type);
  const total = getBehaviorTotal(data);
  const showRows = contentState.kind === 'content' && !!total;

  return (
    <Scorecard title={title} variant={scorecardVariant} total={total}>
      {showRows ? (
        data?.map((datum) => {
          const name = datum.student.voiceName;
          const key = datum.student.id;
          const count = datum.count;

          return <ScorecardRow key={`${type}-${key}`} rowId={key} text={name} count={count} />;
        })
      ) : (
        <Text style={styles.noDataText}>{getScorecardText(type, contentState, undefined, total)}</Text>
      )}
    </Scorecard>
  );
};

interface ReviewScoreCardsProps {
  scorecardData?: ReviewScorecardsResponse | null;
  activeMeetingId?: string;
}

export const ReviewScoreCards = ({ scorecardData, activeMeetingId }: ReviewScoreCardsProps) => {
  const contentState = getMeetingContentState(scorecardData?.meetings, activeMeetingId, scorecardData?.status);
  const scorecards = contentState.kind === 'content' ? contentState.meeting.scorecards : undefined;

  const sortedScorecards = {
    warnings: scorecards?.warnings ? sortStudentData(scorecards.warnings as StudentWithCount[]) : undefined,
    praises: scorecards?.praises ? sortStudentData(scorecards.praises as StudentWithCount[]) : undefined,
  };

  const { warnings, praises } = sortedScorecards;
  const attendanceData = scorecards?.attendance
    ? getAttendanceData(scorecards?.attendance as StudentWithAttendance[])
    : undefined;

  return (
    <View style={styles.container}>
      <View style={styles.scoreCardContainer}>
        <View style={styles.scoreCard}>{renderScorecard('Warned', 'warnings', contentState, warnings)}</View>
        <View style={styles.scoreCard}>{renderScorecard('Praised', 'praises', contentState, praises)}</View>
      </View>
      <View style={styles.attendanceContainer}>
        <AttendanceScorecard attendanceData={attendanceData} contentState={contentState} />
      </View>
    </View>
  );
};

export const styles = StyleSheet.create({
  container: {
    width: '100%',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: theme.spacing.medium,
  },
  attendanceContainer: {
    width: '100%',
  },
  scoreCardContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing.medium,
    paddingHorizontal: theme.spacing.large - 1,
  },
  scoreCard: {
    flex: 1,
  },
  noDataText: {
    fontFamily: theme.fonts.regular,
    fontSize: theme.fontSizes.small,
    fontWeight: theme.fontWeights.regular,
    color: theme.colors.darkGray,
    paddingHorizontal: theme.spacing.large,
    paddingVertical: theme.spacing.small,
  },
});
